import React, { useCallback, useEffect, useState } from "react";
import TaskList from "../../../interfaces/task/TaskList";
import { getAvatarsFromStorage } from "../../../services/fileServices/FileService";
import Avatar from "../../../interfaces/avatar/Avatar";
import { getTasksPdf } from "../../../services/taskServices/TaskPdfService";
import { getTasks } from "../../../services/taskServices/TaskService";
import { useAuth } from "../../../firebase/AuthProvider";
import TasksPageNew from "./TaskPageNew";
import { GetAssigneeAndStatus } from "../../../services/taskServices/TaskLKService";
import { User } from "../../../interfaces/User";
import Participant from "../../../interfaces/Participant";
import { getAllParticipants } from "../../../services/participantServices/ParticipantService";
import Status from "../../../interfaces/task/Status";
import TaskFilters from "../../../interfaces/task/TaskFilters";
import { useLocation } from "react-router-dom";
import { getAllTags } from "../../../services/tagServices/TagService";
import Tag from "../../../interfaces/tag/Tag";
import { toast } from "react-toastify";

const TasksPageComponent: React.FC = () => {
    const { currentUser, currentRoles } = useAuth();

    const [tasks, setTasks] = useState<TaskList[] | null>([]);
    const [totalTasksCount, setTotalTasksCount] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(initialPageSize);
    const [page, setPage] = useState<number>(1);
    const [avatars, setAvatars] = useState<Avatar[] | null>([]);
    const [assignees, setAssignees] = useState<User[]>([]);
    const [participants, setParticipants] = useState<Participant[]>([]);
    const [statuses, setStatuses] = useState<Status[] | null | undefined>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isPdfDownloading, setIsPdfDownloading] = useState<boolean>(false);
    const [sortBy, setSortBy] = useState<string>("");
    const [sortDesc, setSortDesc] = useState<boolean>(false);
    const [tags, setTags] = useState<Tag[]>([]);
    
    const [filters, setFilters] = useState<TaskFilters & { searchTerm?: string; includePendingApprovals?: boolean; userId?: string }>({
        status: [],
        type: [],
        date: [],
        assignees: [],
        participants: [],
        viewAll: false,
        searchTerm: "",
        excludeDate: [],
        excludeStatus: [],
        excludeType: [],
        includePendingApprovals: undefined,
        userId: undefined,
    });

    function initialPageSize() {
        const savedPageSize = localStorage.getItem('tasksPageSize');
        return Number(savedPageSize) || 10;
    }

    useEffect(() => {
        const storedAvatars = getAvatarsFromStorage();
        setAvatars(storedAvatars);
    }, []);

    useEffect(() => {
        const fetchLkValues = async () => {
            if (currentUser !== null) {
                const authToken: string = await currentUser.getIdToken();
                await GetAssigneeAndStatus(authToken).then((data) => {
                    if (data) {
                        setAssignees(data?.users);
                    }
                    setStatuses(data?.statuses);
                });
                await getAllParticipants(authToken).then((data) => {
                    if (data) setParticipants(data);
                });
            }
        };
        fetchLkValues();
        GetAllTags();
    }, [currentUser]);

    const location = useLocation();

    useEffect(() => {
        const savedPageSize = localStorage.getItem('tasksPageSize');
        const params = new URLSearchParams(location.search);
        const pageSizeParam = params.get('pageSize');
        const validPageSizes = [10, 25, 50, 100];

        let finalPageSize = 10;
        if (savedPageSize) {
            const parsed = parseInt(savedPageSize, 10);
            if (validPageSizes.includes(parsed)) finalPageSize = parsed;
        } else if (pageSizeParam) {
            const parsed = parseInt(pageSizeParam, 10);
            if (validPageSizes.includes(parsed)) finalPageSize = parsed;
        }
        setPageSize(finalPageSize);
    }, [location.search, currentUser]);

    useEffect(() => {
        if (currentUser !== null) {
            const fetchTasksForCurrentUser = async () => {
                setIsLoading(true);
                const authToken: string = await currentUser.getIdToken();
                let fetchedTasks;
                let fetchedTotalCount;

                const params = new URLSearchParams(location.search);
                const hasFilterParams = params.has('status') || params.has('assignee') || params.has('participant') || params.has('type') || params.has('date') || params.has('tags') || params.has('searchTerm') || params.has('excludeDate') || params.has('excludeTags') || params.has('excludeStatus') || params.has('excludeType');
                const areFiltersEmpty = !filters.status?.length && !filters.assignees?.length && !filters.participants?.length && !filters.type?.length && !filters.date?.length && !(filters.tags as string[])?.length && !filters.searchTerm && !filters.excludeDate?.length && !filters.excludeStatus?.length && !filters.excludeType?.length;

                if (areFiltersEmpty && hasFilterParams) {
                    setIsLoading(true);
                    return;
                }

                const isOwner = currentRoles?.some((role) => role.name.toLowerCase() === "owner");
                const isSC = currentRoles?.some((role) => role.name === "SC");
                const hasAssigneeFilter = filters.assignees && filters.assignees.length > 0;
                const highestRole = currentRoles?.find((role) =>
                    ["ADMIN", "OWNER", "PROGRAM MANAGER", "SUPERVISOR", "SC", "ASSESSOR"].includes(role.name)
                )?.name || "--";

                const userIdParam = filters.userId !== undefined
                    ? filters.userId
                    : (isOwner
                        ? undefined
                        : (currentRoles?.some((role) => role.name !== "SC")
                            ? undefined
                            : (hasAssigneeFilter ? undefined : currentUser.uid)));

                const finalFilters = { ...filters };

                if (filters.userId && finalFilters.assignees) {
                    finalFilters.assignees = finalFilters.assignees.filter(a => a !== filters.userId);
                }

                // --- REMOVED THE FORCED INITIAL EXCLUSION BLOCK HERE ---

                const includePendingApprovals = filters.includePendingApprovals !== undefined
                    ? filters.includePendingApprovals
                    : (!isSC && highestRole !== "PROGRAM MANAGER" && highestRole !== "--");

                console.log('[TasksPageComponent] Final assignees filter:', finalFilters.assignees);
                console.log('[TasksPageComponent] Final filters object:', JSON.stringify(finalFilters, null, 2));
                console.log('[TasksPageComponent] Final userId filter:', userIdParam);

                let response = await getTasks(
                    authToken,
                    userIdParam,
                    page,
                    pageSize,
                    finalFilters,
                    sortBy || "enddate",
                    sortDesc,
                    filters.viewAll !== undefined ? filters.viewAll : false,
                    includePendingApprovals,
                    filters.searchTerm
                );
                fetchedTotalCount = response?.totalCount;
                fetchedTasks = response?.tasks;

                if (fetchedTasks) {
                    setTasks(fetchedTasks);
                    setTotalTasksCount(fetchedTotalCount || 0);
                }
                setIsLoading(false);
            };
            fetchTasksForCurrentUser();
        }
    }, [currentUser, currentRoles, page, pageSize, sortBy, sortDesc, location, filters]);

    const GetAllTags = async () => {
        if (!currentUser) return
        const authToken = await currentUser.getIdToken();
        const fetchedTags = await getAllTags(authToken);
        if (Array.isArray(fetchedTags)) {
            setTags(fetchedTags);
        }
    }

    const changePage = (page: number) => {
        setPage(page);
    };

    const onFilterChange = useCallback((newFilters: TaskFilters & { searchTerm?: string; includePendingApprovals?: boolean; userId?: string }) => {
        if (JSON.stringify(filters) !== JSON.stringify(newFilters)) {
            setFilters(newFilters);
            setPage(1);
        }
    }, [filters]);

    const handleSort = (column: string, direction?: boolean) => {
        if (sortBy === column) {
            if (direction === undefined) {
                setSortDesc(!sortDesc);
            } else {
                setSortDesc(direction);
            }
        } else {
            setSortBy(column);
            setSortDesc(direction !== undefined ? direction : false);
        }
        setPage(1);
    };

    const handlePdfDownload = async () => {
        if (!currentUser || isPdfDownloading) return;
        const toastId = "tasks-pdf-download";
        try {
            setIsPdfDownloading(true);
            toast.dismiss(toastId);
            toast.info("Generating PDF...", { toastId, autoClose: false });

            const authToken = await currentUser.getIdToken();
            const userId = undefined;
            const blob = await getTasksPdf(authToken, userId, filters, sortBy, sortDesc);

            if (blob) {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = userId ? `tasks_${userId}.pdf` : "tasks.pdf";
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                a.remove();
                toast.dismiss(toastId);
                toast.success("Download successfully.");
            } else {
                toast.dismiss(toastId);
                toast.error("We couldn’t generate this PDF. Please try again.");
            }
        } catch (error) {
            console.error("Failed to download PDF.", error);
            toast.dismiss(toastId);
            toast.error("We couldn’t generate this PDF. Please try again.");
        } finally {
            setIsPdfDownloading(false);
        }
    };

    return (
        <TasksPageNew
            assignees={assignees || []}
            tasks={tasks || []}
            tags={tags}
            participants={participants || []}
            statuses={statuses || []}
            avatars={avatars || []}
            currentPage={page}
            totalTasks={totalTasksCount}
            pageSize={pageSize}
            onPageChange={changePage}
            onFilterChange={onFilterChange}
            isLoading={isLoading}
            setPage={setPage}
            setPageSize={setPageSize}
            handleSort={handleSort}
            onPdfDownload={handlePdfDownload}
            totalTasksCount={totalTasksCount}
            isPdfDownloading={isPdfDownloading}
        />
    );
};

export default TasksPageComponent;